import {
    Alert, Badge, Button, Card, Col, Container, Form, FormControl,
    InputGroup, OverlayTrigger, Row, Spinner, Tooltip
} from "react-bootstrap";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
    changeUserDetail,
    getAllUsers,
    getAllWebhookConnections,
    getUsersFromPayload,
    setAgencyBlocked,
} from "../service/UserService";
import {
    getAgencyDefaults,
    getAgencyOverdueInvoicePdf,
    getBusinessesAndAccounts,
    setBlockOnOverdue,
} from "../service/MyobService";
import * as PropTypes from "prop-types";
import {Link} from "react-router-dom";
import {USER_ROLES, USER_ROLE_BADGE_VARIANTS} from "../constants/userRoles";
import {
    FaEdit, FaFilter, FaSearch, FaSync,
    FaUser, FaUserPlus
} from "react-icons/fa";
import {buildMyobMappingLabels, mappingDisplay} from "./accountant/myobMappingUtils";

const ALL_ROLES = "ALL";
const FILTER_ALL = "ALL";
const FILTER_YES = "YES";
const FILTER_NO = "NO";
const GPS_FILTER_ACTION_REQUIRED = "ACTION_REQUIRED";
const GPS_FILTER_REPORTING = "REPORTING";
const GPS_FILTER_STALE = "STALE";
const GPS_FILTER_OFF = "OFF";

const LOCATION_HEALTH_LABELS = {
    REPORTING_ALWAYS: 'Always',
    REPORTING_APP_OPEN: 'App open',
    REPORTING_STATUS_MISMATCH: 'Reporting · Sync issue',
    OFF: 'Off',
    NEEDS_PERMISSION: 'Needs permission',
    NEVER_REPORTED: 'Never reported',
    STALE: 'Stale',
};
const QUEENSLAND_REGIONS = new Set(['queensland', 'brisbane', 'goldcoast', 'mackay']);
const SPEED_LABELS = {
    VERY_SLOW: 'Very Slow', SLOW: 'Slow', NORMAL: 'Normal', FAST: 'Fast', VERY_FAST: 'Very Fast',
};

function WorkerProfileSummary({row}) {
    if (row.role !== 'WORKER' || !row.worker_profile) return <span className="text-muted">—</span>;
    const profile = row.worker_profile;
    const enabledDays = (profile.working_hours || []).filter(day => day.enabled);
    const sameHours = enabledDays.length > 0
        && enabledDays.every(day => day.start_time === enabledDays[0].start_time && day.end_time === enabledDays[0].end_time);
    const hours = enabledDays.length === 0
        ? 'No working days'
        : `${enabledDays.length}d${sameHours ? ` ${enabledDays[0].start_time}–${enabledDays[0].end_time}` : ' varied'}`;
    const skillNames = (profile.skills || []).slice(0, 2).map(skill => skill.name).join(', ');
    const tierCounts = [1, 2, 3]
        .map(tier => `T${tier}:${(profile.service_areas || []).filter(area => area.tier === tier).length}`)
        .join(' ');
    return (
        <div className="worker-profile-summary">
            <div><Badge bg="info" text="dark">{SPEED_LABELS[profile.speed_tier] || profile.speed_tier} ×{Number(profile.time_coefficient || 1).toFixed(2)}</Badge></div>
            <small>{hours}</small>
            <small>{skillNames || 'No skills'}{(profile.skills || []).length > 2 ? ` +${profile.skills.length - 2}` : ''}</small>
            <small>{tierCounts}</small>
        </div>
    );
}

WorkerProfileSummary.propTypes = {row: PropTypes.object.isRequired};

function matchesBooleanFilter(value, filter) {
    if (filter === FILTER_ALL) return true;
    return filter === FILTER_YES ? value === true : value === false;
}

async function readJson(response, fallback) {
    if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || fallback);
    }
    return response.json();
}

function initialRoleFilter() {
    const requestedRole = new URLSearchParams(window.location.search).get('role')?.toUpperCase();
    return USER_ROLES.includes(requestedRole) ? requestedRole : ALL_ROLES;
}

function UpdateButtonMenu({row}) {
    return (
        <Link
            to={`/manage-user/user-details/${row.username}`}
            className="btn btn-sm btn-outline-primary user-table-edit-link"
        >
            <FaEdit className="me-1" aria-hidden="true" /> Edit
        </Link>
    );
}

UpdateButtonMenu.propTypes = {row: PropTypes.object.isRequired};

function UpdateBlockStatus({row, onUpdated, onError}) {
    const [isUpdating, setIsUpdating] = useState(false);

    function updateBlockStatus(event) {
        const blocked = event.target.checked;
        const previousBlocked = row.blocked;
        setIsUpdating(true);
        onError(null);
        onUpdated(row, blocked);
        const request = row.role === 'AGENT'
            ? setAgencyBlocked(row.cos_customer_id, blocked)
            : changeUserDetail(row.username, {blocked});
        request
            .then(response => {
                if (!response.ok) {
                    return readJson(response, 'Failed to update Blocked status');
                }
            })
            .catch(error => {
                onUpdated(row, previousBlocked);
                onError(error.message || 'Failed to update Blocked status');
            })
            .finally(() => setIsUpdating(false));
    }

    return (
        <div className="d-flex align-items-center" aria-busy={isUpdating}>
            <Form.Check
                type="switch"
                id={`block-${row.username}`}
                aria-label={`Blocked for ${row.username}`}
                checked={row.blocked}
                onChange={updateBlockStatus}
                disabled={isUpdating}
            />
            {isUpdating ? <Spinner animation="border" size="sm" className="ms-2" /> : null}
        </div>
    );
}

UpdateBlockStatus.propTypes = {
    row: PropTypes.object.isRequired,
    onUpdated: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};

function UpdateOverdueBlockStatus({row, mapping, defaultsUnavailable, onUpdated, onError}) {
    const [isUpdating, setIsUpdating] = useState(false);
    const hasMapping = Boolean(mapping?.myob_profile_id && mapping?.myob_business_id && mapping?.myob_contact_id);

    function updateStatus(event) {
        const enabled = event.target.checked;
        const previousEnabled = Boolean(mapping?.block_on_overdue);
        setIsUpdating(true);
        onError(null);
        onUpdated(row.cos_customer_id, enabled);
        setBlockOnOverdue(row.cos_customer_id, enabled)
            .then(response => {
                if (!response.ok) {
                    return readJson(response, 'Failed to update Block on overdue');
                }
            })
            .catch(error => {
                onUpdated(row.cos_customer_id, previousEnabled);
                onError(error.message || 'Failed to update Block on overdue');
            })
            .finally(() => setIsUpdating(false));
    }

    if (row.role !== 'AGENT') return <span>—</span>;
    return (
        <div className="d-flex align-items-center" aria-busy={isUpdating}>
            <Form.Check
                type="switch"
                id={`overdue-block-${row.username}`}
                aria-label={`Block on overdue for ${row.username}`}
                checked={Boolean(mapping?.block_on_overdue)}
                onChange={updateStatus}
                disabled={defaultsUnavailable || isUpdating || (!hasMapping && !mapping?.block_on_overdue)}
                title={!hasMapping ? 'Configure MYOB Contact before enabling automatic blocking' : undefined}
            />
            {isUpdating ? <Spinner animation="border" size="sm" className="ms-2" /> : null}
        </div>
    );
}

UpdateOverdueBlockStatus.propTypes = {
    row: PropTypes.object.isRequired,
    mapping: PropTypes.object,
    defaultsUnavailable: PropTypes.bool.isRequired,
    onUpdated: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};

const currencyFormatter = new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
});

function overdueDisplay(row, mapping, defaultsUnavailable) {
    if (row.role !== 'AGENT') return '—';
    if (defaultsUnavailable) return 'Unavailable';
    if (!mapping?.overdue_last_success_at) return mapping?.overdue_last_error ? 'Unavailable' : 'Not checked';
    const stale = Boolean(mapping.overdue_last_error
        && mapping.overdue_last_attempt_at !== mapping.overdue_last_success_at);
    if (!mapping.is_overdue) return stale ? 'No overdue (stale)' : 'No overdue';
    const days = mapping.overdue_business_days ?? 0;
    const invoices = mapping.overdue_invoice_count ?? 0;
    const label = `${invoices} invoice${invoices === 1 ? '' : 's'} · ${days}d · ${currencyFormatter.format(mapping.overdue_balance ?? 0)}`;
    return stale ? `${label} (stale)` : label;
}

function overdueInvoiceHref(cosCustomerId, invoiceUid) {
    return `/api/myob/agency-defaults/${encodeURIComponent(cosCustomerId)}/overdue-invoices/${encodeURIComponent(invoiceUid)}/pdf`;
}

function OverdueInvoiceLink({cosCustomerId, invoice, onError}) {
    const [isLoading, setIsLoading] = useState(false);
    const invoiceLabel = invoice.number || invoice.uid || 'Invoice';
    if (!invoice.uid) return <span title={invoiceLabel}>{invoiceLabel}</span>;
    const href = overdueInvoiceHref(cosCustomerId, invoice.uid);

    async function openInvoice(event) {
        event.preventDefault();
        if (!invoice.uid || isLoading) return;
        setIsLoading(true);
        onError(null);
        // Open synchronously from the click so the browser does not block the
        // tab while the authenticated PDF request is in flight. Applying
        // noopener after opening keeps the new tab isolated without relying on
        // the `noopener` feature, which can make window.open return null in
        // some browsers.
        const popup = window.open('', '_blank');
        if (!popup) {
            onError('Please allow pop-ups to open the MYOB Invoice');
            setIsLoading(false);
            return;
        }
        popup.opener = null;
        try {
            const response = await getAgencyOverdueInvoicePdf(cosCustomerId, invoice.uid);
            if (!response.ok) {
                const body = await response.json().catch(() => null);
                throw new Error(body?.message || 'Failed to load MYOB Invoice PDF');
            }
            const pdfUrl = URL.createObjectURL(await response.blob());
            popup.location.href = pdfUrl;
            window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
        } catch (error) {
            popup.close();
            onError(error.message || 'Failed to load MYOB Invoice PDF');
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <a
            href={href}
            onClick={openInvoice}
            title={invoice.uid ? `Open MYOB Invoice ${invoiceLabel}` : invoiceLabel}
            aria-label={`Open MYOB Invoice ${invoiceLabel}`}
            className={isLoading ? 'text-muted' : undefined}
        >
            {invoiceLabel}
        </a>
    );
}

OverdueInvoiceLink.propTypes = {
    cosCustomerId: PropTypes.string.isRequired,
    invoice: PropTypes.shape({uid: PropTypes.string, number: PropTypes.string}).isRequired,
    onError: PropTypes.func.isRequired,
};

function overdueInvoiceSearchText(mapping) {
    return (mapping?.overdue_invoices || [])
        .map(invoice => `${invoice.number || ''} ${invoice.uid || ''}`)
        .join(' ');
}

function OverdueInvoicesCell({row, mapping, defaultsUnavailable, onError}) {
    const value = overdueDisplay(row, mapping, defaultsUnavailable);
    if (row.role !== 'AGENT' || !mapping?.is_overdue || !mapping.overdue_invoices?.length) {
        return <span title={mapping?.overdue_last_error || undefined}>{value}</span>;
    }
    return (
        <div className="myob-overdue-cell">
            <div title={mapping.overdue_last_error || undefined}>{value}</div>
            <div className="small mt-1">
                {mapping.overdue_invoices.map((invoice, index) => (
                    <span key={invoice.uid || `${invoice.number}-${index}`}>
                        {index > 0 ? ', ' : 'Invoices: '}
                        <OverdueInvoiceLink
                            cosCustomerId={row.cos_customer_id}
                            invoice={invoice}
                            onError={onError}
                        />
                    </span>
                ))}
            </div>
        </div>
    );
}

OverdueInvoicesCell.propTypes = {
    row: PropTypes.object.isRequired,
    mapping: PropTypes.object,
    defaultsUnavailable: PropTypes.bool.isRequired,
    onError: PropTypes.func.isRequired,
};

function locationStatusLabel(row) {
    if (row.role !== 'WORKER' && row.role !== 'SUPERVISOR') return '—';
    const tracking = row.location_tracking;
    if (tracking?.health_status && LOCATION_HEALTH_LABELS[tracking.health_status]) {
        return LOCATION_HEALTH_LABELS[tracking.health_status];
    }
    if (!tracking || !tracking.enabled || tracking.display_mode === 'NO') {
        if (tracking?.enabled && tracking.mode === 'PERMISSION_DENIED') return 'Needs permission';
        return 'Off';
    }
    if (tracking.display_mode === 'ALWAYS' || tracking.mode === 'ACTIVE') return 'Always';
    if (tracking.display_mode === 'APP_OPEN' || tracking.mode === 'FOREGROUND_ONLY') return 'App open';
    return 'Needs permission';
}

function locationStatusVariant(label) {
    if (label === 'Always') return 'success';
    if (label === 'App open') return 'success';
    if (label === 'Reporting · Sync issue') return 'warning';
    if (label === 'Stale') return 'danger';
    if (label === 'Needs permission') return 'danger';
    if (label === 'Never reported') return 'secondary';
    if (label === 'Off') return 'secondary';
    return 'light';
}

function locationTimeZone(row) {
    return QUEENSLAND_REGIONS.has(row.region?.toLowerCase())
        ? 'Australia/Brisbane'
        : 'Australia/Melbourne';
}

function formatLocationTime(row, timestamp) {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('en-AU', {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: locationTimeZone(row),
    }).format(date);
}

function locationReportLabel(row) {
    if (row.role !== 'WORKER' && row.role !== 'SUPERVISOR') return null;
    const tracking = row.location_tracking;
    const lastSentAt = tracking?.last_sent_at;
    const lastAttemptAt = tracking?.last_attempt_at;
    const lastSentText = lastSentAt ? formatLocationTime(row, lastSentAt) : null;
    const lastAttemptText = lastAttemptAt ? formatLocationTime(row, lastAttemptAt) : null;
    const hasUnreportedError = Boolean(
        tracking?.last_error && lastAttemptAt !== lastSentAt,
    );

    if (hasUnreportedError) {
        return lastSentText ? `Report error; last report ${lastSentText}` : 'Report error';
    }
    if (lastSentText) return `Last report ${lastSentText}`;
    if (lastAttemptText) return `Last attempt ${lastAttemptText}`;
    return 'No report yet';
}

function LocationStatusCell({row}) {
    const label = locationStatusLabel(row);
    if (label === '—') return <span>—</span>;
    const reportLabel = locationReportLabel(row);
    return (
        <div>
            <Badge bg={locationStatusVariant(label)}>{label}</Badge>
            {row.location_tracking?.action_required ? (
                <Badge bg="danger" className="ms-1">Action required</Badge>
            ) : row.location_tracking?.required_today ? (
                <Badge bg="light" text="dark" className="ms-1">Required today</Badge>
            ) : null}
            <div
                className="small text-muted mt-1"
                title={row.location_tracking?.last_error || undefined}
            >
                {reportLabel}
            </div>
        </div>
    );
}

LocationStatusCell.propTypes = {row: PropTypes.object.isRequired};

const tableCollator = new Intl.Collator('en-AU', {numeric: true, sensitivity: 'base'});
const rowsPerPageOptions = [10, 25, 50, 100];

function displayTableValue(value) {
    return value === null || value === undefined || value === '' ? '—' : String(value);
}

function TruncatedTableText({value, className = ''}) {
    const text = displayTableValue(value);
    return (
        <span className={`user-table-ellipsis ${className}`.trim()} title={text}>
            {text}
        </span>
    );
}

TruncatedTableText.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
    ]),
    className: PropTypes.string,
};

function StackedTableText({firstValue, secondValue}) {
    return (
        <div className="user-table-stacked">
            <TruncatedTableText value={firstValue}/>
            <TruncatedTableText value={secondValue}/>
        </div>
    );
}

StackedTableText.propTypes = {
    firstValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    secondValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function compareTableValues(left, right) {
    if (typeof left === 'boolean' || typeof right === 'boolean') {
        return Number(Boolean(left)) - Number(Boolean(right));
    }
    if (typeof left === 'number' && typeof right === 'number') return left - right;
    return tableCollator.compare(displayTableValue(left), displayTableValue(right));
}

function activePropertyCount(row) {
    return row.role === 'AGENT' && typeof row.active_property_count === 'number'
        ? row.active_property_count : null;
}

function UserTablePagination({currentPage, totalPages, totalRows, rowsPerPage, onPageChange, onRowsPerPageChange}) {
    const firstRow = totalRows === 0 ? 0 : ((currentPage - 1) * rowsPerPage) + 1;
    const lastRow = Math.min(currentPage * rowsPerPage, totalRows);

    return (
        <div className="user-table-pagination">
            <div className="user-table-page-size">
                <label htmlFor="manage-users-rows-per-page">Rows per page:</label>
                <select
                    id="manage-users-rows-per-page"
                    name="manage-users-rows-per-page"
                    value={rowsPerPage}
                    onChange={event => onRowsPerPageChange(Number(event.target.value))}
                >
                    {rowsPerPageOptions.map(option => <option key={option} value={option}>{option}</option>)}
                </select>
            </div>
            <span className="user-table-pagination-range" aria-live="polite">
                {firstRow}–{lastRow} of {totalRows}
            </span>
            <nav className="user-table-page-buttons" aria-label="User table pages">
                <button
                    type="button"
                    aria-label="First page"
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(1)}
                >
                    «
                </button>
                <button
                    type="button"
                    aria-label="Previous page"
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                >
                    ‹
                </button>
                <button
                    type="button"
                    aria-label="Next page"
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                >
                    ›
                </button>
                <button
                    type="button"
                    aria-label="Last page"
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(totalPages)}
                >
                    »
                </button>
            </nav>
        </div>
    );
}

UserTablePagination.propTypes = {
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    totalRows: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
};

export default function ManageUser() {
    const [tableData, setTableData] = useState([]);
    const [roleFilterSelection, setRoleFilterSelection] = useState(initialRoleFilter);
    const [overdueFilter, setOverdueFilter] = useState(FILTER_ALL);
    const [autoBlockFilter, setAutoBlockFilter] = useState(FILTER_ALL);
    const [blockedFilter, setBlockedFilter] = useState(FILTER_ALL);
    const [gpsFilter, setGpsFilter] = useState(FILTER_ALL);
    const [searchText, setSearchText] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [myobWarning, setMyobWarning] = useState(null);
    const [defaultsUnavailable, setDefaultsUnavailable] = useState(false);
    const [contactsUnavailable, setContactsUnavailable] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [webhookConnections, setWebhookConnections] = useState([]);
    const [agencyDefaults, setAgencyDefaults] = useState([]);
    const [businessesAndAccounts, setBusinessesAndAccounts] = useState({});
    const [sortState, setSortState] = useState({key: 'active-property-count', direction: 'desc'});
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

    const refreshUsers = useCallback(async ({silent = false} = {}) => {
        if (silent) {
            try {
                const response = await getAllUsers({includeActivePropertyCount: true});
                setTableData(getUsersFromPayload(await readJson(response, 'Failed to refresh GPS health')));
                setError(null);
            } catch (refreshError) {
                setError(refreshError.message || 'An error occurred while refreshing GPS health');
            }
            return;
        }
        if (!silent) {
            setIsLoading(true);
            setError(null);
            setMyobWarning(null);
            setActionError(null);
        }

        const results = await Promise.allSettled([
            getAllUsers({includeActivePropertyCount: true}).then(response => readJson(response, 'Failed to load users')),
            getAllWebhookConnections().then(response => readJson(response, 'Failed to load Guesty integrations')),
            getAgencyDefaults().then(response => readJson(response, 'Failed to load Agency MYOB defaults')),
            getBusinessesAndAccounts('false').then(response => readJson(response, 'Failed to load MYOB contacts')),
        ]);

        const [usersResult, webhooksResult, defaultsResult, businessesResult] = results;
        if (usersResult.status === 'fulfilled') {
            setTableData(getUsersFromPayload(usersResult.value));
        } else {
            setError(usersResult.reason.message || 'An error occurred while fetching users');
        }

        if (webhooksResult.status === 'fulfilled') {
            const payload = webhooksResult.value;
            setWebhookConnections(payload.success && payload.webhooks ? payload.webhooks : []);
        }

        const myobErrors = [];
        if (defaultsResult.status === 'fulfilled') {
            setAgencyDefaults(defaultsResult.value);
            setDefaultsUnavailable(false);
        } else {
            setAgencyDefaults([]);
            setDefaultsUnavailable(true);
            setOverdueFilter(FILTER_ALL);
            setAutoBlockFilter(FILTER_ALL);
            setCurrentPage(1);
            myobErrors.push(defaultsResult.reason.message);
        }
        if (businessesResult.status === 'fulfilled') {
            setBusinessesAndAccounts(businessesResult.value);
            setContactsUnavailable(false);
        } else {
            setBusinessesAndAccounts({});
            setContactsUnavailable(true);
            myobErrors.push(businessesResult.reason.message);
        }
        setMyobWarning(myobErrors.length > 0
            ? `${myobErrors.join('. ')}. User management remains available.`
            : null);
        if (!silent) setIsLoading(false);
    }, []);

    useEffect(() => {
        refreshUsers();
    }, [refreshUsers]);

    const mappingByCustomerId = useMemo(
        () => new Map(agencyDefaults.map(mapping => [mapping.cos_customer_id, mapping])),
        [agencyDefaults]
    );
    const labels = useMemo(
        () => buildMyobMappingLabels([], businessesAndAccounts),
        [businessesAndAccounts]
    );
    const webhookCustomerIds = useMemo(
        () => new Set(webhookConnections.map(webhook => webhook.cos_customer_id)),
        [webhookConnections]
    );

    const myobContactFor = useCallback((row) => {
        if (row.role !== 'AGENT') return '—';
        if (defaultsUnavailable || contactsUnavailable) return 'Unavailable';
        return mappingDisplay(mappingByCustomerId.get(row.cos_customer_id), labels);
    }, [contactsUnavailable, defaultsUnavailable, labels, mappingByCustomerId]);

    const myobOverdueFor = useCallback((row) => overdueDisplay(
        row,
        mappingByCustomerId.get(row.cos_customer_id),
        defaultsUnavailable
    ), [defaultsUnavailable, mappingByCustomerId]);

    const updateBlockedState = useCallback((changedRow, blocked) => {
        setTableData(current => current.map(row => {
            const matches = changedRow.role === 'AGENT'
                ? row.role === 'AGENT' && row.cos_customer_id === changedRow.cos_customer_id
                : row.username === changedRow.username;
            return matches ? {...row, blocked} : row;
        }));
    }, []);

    const updateOverdueBlockState = useCallback((cosCustomerId, enabled) => {
        setAgencyDefaults(current => current.map(mapping => mapping.cos_customer_id === cosCustomerId
            ? {...mapping, block_on_overdue: enabled}
            : mapping));
    }, []);

    const filteredData = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        return tableData.filter(item => {
            if (roleFilterSelection !== ALL_ROLES && item.role !== roleFilterSelection) return false;
            const mapping = mappingByCustomerId.get(item.cos_customer_id);
            if (overdueFilter !== FILTER_ALL
                && (item.role !== 'AGENT' || !matchesBooleanFilter(mapping?.is_overdue, overdueFilter))) {
                return false;
            }
            if (autoBlockFilter !== FILTER_ALL
                && (item.role !== 'AGENT'
                    || !matchesBooleanFilter(Boolean(mapping?.block_on_overdue), autoBlockFilter))) {
                return false;
            }
            if (!matchesBooleanFilter(Boolean(item.blocked), blockedFilter)) {
                return false;
            }
            const health = item.location_tracking?.health_status;
            if (gpsFilter === GPS_FILTER_ACTION_REQUIRED
                && item.location_tracking?.action_required !== true) return false;
            if (gpsFilter === GPS_FILTER_REPORTING
                && !health?.startsWith('REPORTING_')) return false;
            if (gpsFilter === GPS_FILTER_STALE && health !== 'STALE') return false;
            if (gpsFilter === GPS_FILTER_OFF && health !== 'OFF') return false;
            if (!query) return true;
            return [
                item.username,
                item.agent_name,
                item.cos_customer_name,
                item.phone,
                item.email,
                myobContactFor(item),
                myobOverdueFor(item),
                overdueInvoiceSearchText(mappingByCustomerId.get(item.cos_customer_id)),
                locationStatusLabel(item),
                item.worker_profile?.speed_tier,
                ...(item.worker_profile?.skills || []).map(skill => skill.name),
                ...(item.worker_profile?.service_areas || []).flatMap(area => [area.name, area.postcode]),
            ].some(value => value?.toLowerCase().includes(query));
        });
    }, [autoBlockFilter, blockedFilter, gpsFilter, mappingByCustomerId, myobContactFor, myobOverdueFor,
        overdueFilter, roleFilterSelection, searchText, tableData]);

    const columns = useMemo(() => [
        {
            key: 'id',
            name: 'ID',
            className: 'user-table-col--id',
            sortable: true,
            sortValue: row => row.cos_customer_id,
            render: row => <span className="user-table-id" title={displayTableValue(row.cos_customer_id)}>
                {displayTableValue(row.cos_customer_id)}
            </span>,
        },
        {
            key: 'username',
            name: 'Username',
            className: 'user-table-col--username',
            sortable: true,
            sortValue: row => row.username,
            render: row => <TruncatedTableText value={row.username} className="fw-semibold"/>,
        },
        {
            key: 'blocked',
            name: 'Blocked',
            className: 'user-table-col--blocked',
            sortable: true,
            sortValue: row => row.blocked,
            render: row => <UpdateBlockStatus row={row} onUpdated={updateBlockedState} onError={setActionError}/>,
        },
        {
            key: 'name-agent',
            name: 'Name / Agent Name',
            className: 'user-table-col--name-agent',
            mobileVisible: row => Boolean(row.cos_customer_name || row.agent_name),
            sortable: true,
            sortValue: row => row.cos_customer_name,
            render: row => <StackedTableText
                firstValue={row.cos_customer_name}
                secondValue={row.agent_name}
            />,
        },
        {
            key: 'active-property-count',
            name: 'Active properties',
            className: 'user-table-col--active-properties',
            mobileVisible: row => row.role === 'AGENT',
            sortable: true,
            sortValue: activePropertyCount,
            render: row => <span className="user-table-id">
                {row.role === 'AGENT' ? activePropertyCount(row) ?? 'Unavailable' : '—'}
            </span>,
        },
        {
            key: 'role',
            name: 'Role',
            className: 'user-table-col--role',
            sortable: true,
            sortValue: row => row.role,
            render: row => <Badge bg={USER_ROLE_BADGE_VARIANTS[row.role] || 'secondary'}>{row.role}</Badge>,
        },
        {
            key: 'location',
            name: 'Location',
            className: 'user-table-col--location',
            mobileVisible: row => row.role === 'WORKER' || row.role === 'SUPERVISOR',
            sortable: true,
            sortValue: row => `${row.location_tracking?.action_required ? '0' : '1'}-`
                + `${row.location_tracking?.health_status || ''}-`
                + `${row.location_tracking?.last_sent_at || row.location_tracking?.last_attempt_at || ''}`,
            render: row => <LocationStatusCell row={row}/>,
        },
        {
            key: 'worker-profile',
            name: 'Worker Profile',
            className: 'user-table-col--worker-profile',
            mobileVisible: row => row.role === 'WORKER',
            sortable: true,
            sortValue: row => `${row.worker_profile?.speed_tier || ''}-${row.worker_profile?.skills?.length || 0}`,
            render: row => <WorkerProfileSummary row={row}/>,
        },
        {
            key: 'phone-email',
            name: 'Phone / Email',
            className: 'user-table-col--phone-email',
            mobileVisible: row => Boolean(row.phone || row.email),
            sortable: true,
            sortValue: row => row.phone,
            render: row => <StackedTableText
                firstValue={row.phone}
                secondValue={row.email}
            />,
        },
        {
            key: 'region',
            name: 'Region',
            className: 'user-table-col--region',
            mobileVisible: row => Boolean(row.region),
            sortable: true,
            sortValue: row => row.region,
            render: row => <TruncatedTableText value={row.region}/>,
        },
        {
            key: 'myob-contact',
            name: 'MYOB Contact',
            className: 'user-table-col--myob-contact',
            mobileVisible: row => row.role === 'AGENT',
            sortable: true,
            sortValue: myobContactFor,
            render: row => <TruncatedTableText value={myobContactFor(row)}/>,
        },
        {
            key: 'myob-overdue',
            name: 'MYOB Overdue',
            className: 'user-table-col--myob-overdue',
            mobileVisible: row => row.role === 'AGENT',
            sortable: true,
            sortValue: myobOverdueFor,
            render: row => (
                <div className="user-table-overdue-content">
                    <OverdueInvoicesCell
                        row={row}
                        mapping={mappingByCustomerId.get(row.cos_customer_id)}
                        defaultsUnavailable={defaultsUnavailable}
                        onError={setActionError}
                    />
                </div>
            ),
        },
        {
            key: 'auto-block',
            name: 'Auto Block',
            className: 'user-table-col--auto-block',
            mobileVisible: row => row.role === 'AGENT',
            sortable: true,
            sortValue: row => Boolean(mappingByCustomerId.get(row.cos_customer_id)?.block_on_overdue),
            render: row => <UpdateOverdueBlockStatus
                row={row}
                mapping={mappingByCustomerId.get(row.cos_customer_id)}
                defaultsUnavailable={defaultsUnavailable}
                onUpdated={updateOverdueBlockState}
                onError={setActionError}
            />,
        },
        {
            key: 'integration',
            name: 'Integration',
            className: 'user-table-col--integration',
            sortable: false,
            render: row => {
                const hasGuestyIntegration = webhookCustomerIds.has(row.cos_customer_id);
                return (
                    <Link
                        to={`/manage-user/guesty-integration/${row.cos_customer_id}`}
                        className="user-table-integration-link"
                        aria-label={`${hasGuestyIntegration ? 'Manage' : 'Set up'} Guesty integration for ${row.username}`}
                    >
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{hasGuestyIntegration ? "Manage Guesty Integration" : "Setup Guesty Integration"}</Tooltip>}
                        >
                            <span>
                                <img
                                    src="/guesty_log.jpg"
                                    alt=""
                                    aria-hidden="true"
                                    width={24}
                                    height={24}
                                    loading="lazy"
                                    decoding="async"
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        objectFit: 'contain',
                                        opacity: hasGuestyIntegration ? 1 : 0.3,
                                        filter: hasGuestyIntegration ? 'none' : 'grayscale(100%)',
                                    }}
                                />
                            </span>
                        </OverlayTrigger>
                        <span className="user-table-integration-text">
                            {hasGuestyIntegration ? 'Manage Guesty' : 'Set up Guesty'}
                        </span>
                    </Link>
                );
            },
        },
        {
            key: 'actions',
            name: 'Actions',
            className: 'user-table-col--actions',
            sortable: false,
            render: row => <UpdateButtonMenu row={row}/>,
        },
    ], [defaultsUnavailable, mappingByCustomerId, myobContactFor, myobOverdueFor,
        updateBlockedState, updateOverdueBlockState, webhookCustomerIds]);

    const sortedData = useMemo(() => {
        if (!sortState.key) return filteredData;
        const sortColumn = columns.find(column => column.key === sortState.key);
        if (!sortColumn?.sortValue) return filteredData;
        const direction = sortState.direction === 'asc' ? 1 : -1;
        return [...filteredData].sort((left, right) => {
            const leftValue = sortColumn.sortValue(left);
            const rightValue = sortColumn.sortValue(right);
            if (sortState.key === 'active-property-count') {
                if (leftValue === null && rightValue !== null) return 1;
                if (rightValue === null && leftValue !== null) return -1;
                return direction * ((leftValue ?? 0) - (rightValue ?? 0))
                    || tableCollator.compare(left.username, right.username);
            }
            return direction * compareTableValues(leftValue, rightValue);
        });
    }, [columns, filteredData, sortState]);

    const totalPages = Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
    const visiblePage = Math.min(currentPage, totalPages);
    const pagedData = useMemo(
        () => sortedData.slice((visiblePage - 1) * rowsPerPage, visiblePage * rowsPerPage),
        [rowsPerPage, sortedData, visiblePage]
    );
    const idColumnWidth = useMemo(() => {
        const longestId = tableData.reduce(
            (longest, row) => Math.max(longest, displayTableValue(row.cos_customer_id).length),
            2,
        );
        return `${Math.max(5, longestId + 2)}ch`;
    }, [tableData]);

    function toggleSort(column) {
        if (!column.sortable) return;
        setCurrentPage(1);
        setSortState(current => current.key === column.key
            ? {key: column.key, direction: current.direction === 'asc' ? 'desc' : 'asc'}
            : {key: column.key, direction: column.key === 'active-property-count' ? 'desc' : 'asc'});
    }

    function toggleRoleFilter(event) {
        setRoleFilterSelection(event.target.id);
        setCurrentPage(1);
    }

    function updateTriStateFilter(setter) {
        return event => {
            setter(event.target.value);
            setCurrentPage(1);
        };
    }

    function selectMobileSort(event) {
        setCurrentPage(1);
        setSortState({key: event.target.value || null, direction: event.target.value === 'active-property-count' ? 'desc' : 'asc'});
    }

    function toggleMobileSortDirection() {
        if (!sortState.key) return;
        setCurrentPage(1);
        setSortState(current => ({
            ...current,
            direction: current.direction === 'asc' ? 'desc' : 'asc',
        }));
    }

    return (
        <Container fluid className="manage-user-page py-3 py-md-4">
            <Card className="shadow-sm">
                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <h1 className="h5 mb-0">
                        <FaUser className="me-2" aria-hidden="true" /> Manage Users
                    </h1>
                    <Button variant="light" size="sm" onClick={() => refreshUsers()} disabled={isLoading}>
                        <FaSync className={isLoading ? "me-2 fa-spin" : "me-2"} aria-hidden="true" /> Refresh
                    </Button>
                </Card.Header>
                <Card.Body>
                    {error ? <Alert variant="danger" className="mb-4" role="alert">{error}</Alert> : null}
                    {actionError ? <Alert variant="danger" className="mb-4" role="alert">{actionError}</Alert> : null}
                    {myobWarning ? <Alert variant="warning" className="mb-4" role="status">{myobWarning}</Alert> : null}

                    <Row className="manage-user-toolbar mb-4 align-items-center">
                        <Col lg className="order-3 order-lg-1 mt-3 mt-lg-0">
                            <Card className="border-light bg-light">
                                <Card.Body className="py-2">
                                    <div className="manage-user-filter-content d-flex flex-wrap align-items-center gap-2">
                                        <FaFilter className="me-2 text-primary" aria-hidden="true" />
                                        <span className="fw-bold me-2">Filter by Role:</span>
                                        <Form className="d-flex flex-wrap align-items-center">
                                            <div className="d-flex flex-wrap">
                                                {USER_ROLES.map(role => (
                                                    <Form.Check
                                                        key={role}
                                                        inline
                                                        label={role}
                                                        name="role-filter"
                                                        type="radio"
                                                        checked={roleFilterSelection === role}
                                                        onChange={toggleRoleFilter}
                                                        id={role}
                                                        className="me-2"
                                                    />
                                                ))}
                                                <Form.Check
                                                    inline
                                                    name="role-filter"
                                                    label="All"
                                                    type="radio"
                                                    checked={roleFilterSelection === ALL_ROLES}
                                                    onChange={toggleRoleFilter}
                                                    id={ALL_ROLES}
                                                    className="me-2"
                                                />
                                            </div>
                                        </Form>
                                        {[
                                            ['Overdue', overdueFilter, setOverdueFilter, defaultsUnavailable],
                                            ['Auto Block', autoBlockFilter, setAutoBlockFilter, defaultsUnavailable],
                                            ['Blocked', blockedFilter, setBlockedFilter, false],
                                        ].map(([label, value, setter, disabled]) => (
                                            <Form.Group key={label} className="d-flex align-items-center ms-md-2">
                                                <Form.Label className="mb-0 me-1 small fw-semibold">{label}</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    aria-label={`${label} filter`}
                                                    value={value}
                                                    onChange={updateTriStateFilter(setter)}
                                                    disabled={disabled}
                                                    style={{width: 'auto'}}
                                                >
                                                    <option value={FILTER_ALL}>All</option>
                                                    <option value={FILTER_YES}>Yes</option>
                                                    <option value={FILTER_NO}>No</option>
                                                </Form.Select>
                                            </Form.Group>
                                        ))}
                                        <Form.Group className="d-flex align-items-center ms-md-2">
                                            <Form.Label className="mb-0 me-1 small fw-semibold">GPS</Form.Label>
                                            <Form.Select
                                                size="sm"
                                                aria-label="GPS filter"
                                                value={gpsFilter}
                                                onChange={event => {
                                                    setGpsFilter(event.target.value);
                                                    setCurrentPage(1);
                                                }}
                                                style={{width: 'auto'}}
                                            >
                                                <option value={FILTER_ALL}>All</option>
                                                <option value={GPS_FILTER_ACTION_REQUIRED}>Action required</option>
                                                <option value={GPS_FILTER_REPORTING}>Reporting</option>
                                                <option value={GPS_FILTER_STALE}>Stale</option>
                                                <option value={GPS_FILTER_OFF}>Off</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col sm="auto" className="order-2 order-lg-2 mt-3 mt-sm-0">
                            <Link to="/manage-user/add-user" className="btn btn-success text-nowrap manage-user-add-link">
                                <FaUserPlus className="me-2" aria-hidden="true" /> Add New User
                            </Link>
                        </Col>
                        <Col sm lg={2} className="order-1 order-lg-3">
                            <InputGroup>
                                <InputGroup.Text aria-hidden="true"><FaSearch /></InputGroup.Text>
                                <FormControl
                                    value={searchText}
                                    onChange={event => {
                                        setSearchText(event.target.value);
                                        setCurrentPage(1);
                                    }}
                                    type="search"
                                    name="user-search"
                                    autoComplete="off"
                                    spellCheck={false}
                                    placeholder="Search users…"
                                    aria-label="Search"
                                />
                            </InputGroup>
                        </Col>
                    </Row>

                    <div
                        className="manage-user-table"
                        role="table"
                        aria-label="Manage users"
                        aria-colcount={columns.length}
                        style={{'--user-id-width': idColumnWidth}}
                    >
                        {!isLoading ? (
                            <div className="user-table-mobile-toolbar">
                                <span className="user-table-result-count" aria-live="polite">
                                    {sortedData.length} {sortedData.length === 1 ? 'user' : 'users'}
                                </span>
                                <div className="user-table-mobile-sort">
                                    <label htmlFor="manage-users-mobile-sort">Sort by</label>
                                    <select
                                        id="manage-users-mobile-sort"
                                        name="manage-users-mobile-sort"
                                        aria-label="Sort users by"
                                        value={sortState.key || ''}
                                        onChange={selectMobileSort}
                                    >
                                        <option value="">Default</option>
                                        {columns.filter(column => column.sortable).map(column => (
                                            <option key={column.key} value={column.key}>{column.name}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="user-table-sort-direction"
                                        aria-label={`Sort ${sortState.direction === 'asc' ? 'descending' : 'ascending'}`}
                                        disabled={!sortState.key}
                                        onClick={toggleMobileSortDirection}
                                    >
                                        {sortState.direction === 'asc' ? '↑' : '↓'}
                                    </button>
                                </div>
                            </div>
                        ) : null}
                        <div className="user-table-row user-table-head" role="row">
                            {columns.map((column, columnIndex) => {
                                const isSorted = sortState.key === column.key;
                                const sortDirection = isSorted ? sortState.direction : undefined;
                                return (
                                    <div
                                        key={column.key}
                                        className={`user-table-cell user-table-header-cell ${column.className}`}
                                        role="columnheader"
                                        aria-colindex={columnIndex + 1}
                                        aria-sort={sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : 'none'}
                                    >
                                        {column.sortable ? (
                                            <button
                                                type="button"
                                                className="user-table-header-button"
                                                onClick={() => toggleSort(column)}
                                            >
                                                <span className="user-table-header-text">{column.name}</span>
                                                {isSorted ? <span aria-hidden="true">{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span> : null}
                                            </button>
                                        ) : (
                                            <span className="user-table-header-text">{column.name}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="user-table-body">
                            {isLoading ? (
                                <div className="user-table-state" role="row">
                                    <div className="user-table-state-cell" role="cell" aria-live="polite">
                                        <Spinner animation="border" variant="primary" aria-hidden="true"/>
                                        <span className="visually-hidden">Loading users…</span>
                                    </div>
                                </div>
                            ) : pagedData.length > 0 ? pagedData.map((row, rowIndex) => (
                                <div className="user-table-row user-table-data-row" role="row" key={`${row.username}-${rowIndex}`}>
                                    {columns.map((column, columnIndex) => {
                                        const isMobileHidden = column.mobileVisible && !column.mobileVisible(row);
                                        return (
                                            <div
                                                key={column.key}
                                                className={`user-table-cell ${column.className}${isMobileHidden ? ' user-table-cell--mobile-hidden' : ''}`}
                                                role="cell"
                                                aria-colindex={columnIndex + 1}
                                                data-column-key={column.key}
                                            >
                                                <span className="user-table-mobile-label">
                                                    {column.name}
                                                </span>
                                                {column.render(row)}
                                            </div>
                                        );
                                    })}
                                </div>
                            )) : (
                                <div className="user-table-state" role="row">
                                    <div className="user-table-state-cell" role="cell">
                                        No users found matching your criteria
                                    </div>
                                </div>
                            )}
                        </div>
                        {!isLoading ? (
                            <UserTablePagination
                                currentPage={visiblePage}
                                totalPages={totalPages}
                                totalRows={sortedData.length}
                                rowsPerPage={rowsPerPage}
                                onPageChange={setCurrentPage}
                                onRowsPerPageChange={value => {
                                    setRowsPerPage(value);
                                    setCurrentPage(1);
                                }}
                            />
                        ) : null}
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
}
